/**
 * Los dibujos del mapa de esquemas — HU-09.
 *
 * POR QUÉ ES SVG Y NO DIVS. La CSP del servidor es `style-src 'self'`: una barra
 * hecha con `style={{ width }}` funciona con Vite delante y la bloquea el
 * navegador en producción. En SVG el tamaño va en atributos —`d`, `width`, `r`—,
 * que no son estilos y a los que la CSP no llega. El color sí va por clase, en
 * la hoja de estilos, para no repartir la paleta por dos sitios.
 *
 * AQUÍ SOLO SE DIBUJA. La regla de qué color le toca a cada dimensión está en
 * `dimensiones.ts`, que no tiene JSX y por eso se puede probar desde `tests/`.
 *
 * AQUÍ VIVIÓ LA ROSA —los quince esquemas en un círculo de pétalos— hasta agosto
 * de 2026. Se cambió por las cinco puertas, un anillo por área, porque se
 * entendían sin leyenda. En octubre de 2026 los anillos pasaron a ser los
 * cuatro esquemas principales, a pedido del cliente (ver `Mapa.tsx`).
 */

/** Con una decimal y coma, igual que los puntajes del resto de la pantalla. */
const numero = new Intl.NumberFormat('es-CO', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/* ========================================================================== */

const ANILLO_LADO = 120;
const ANILLO_CENTRO = ANILLO_LADO / 2;
const ANILLO_RADIO = 50;
/** El largo total del arco. Con él se recorta cuánto se pinta (`dasharray`). */
const ANILLO_VUELTA = 2 * Math.PI * ANILLO_RADIO;

/**
 * El anillo de un esquema principal: un arco que crece con `valor` y el mismo
 * valor escrito en medio.
 *
 * EL ARCO EMPIEZA EN EL MÍNIMO DE LA ESCALA, NO EN CERO, por lo mismo que la
 * rosa que hubo antes: la escala va de 1 a 6 y nadie puede sacar 0. Con el
 * arranque en cero, el arco más corto posible ya ocuparía un sexto de la
 * vuelta y todos los anillos se verían medio llenos.
 *
 * `stroke-dasharray` VA EN ATRIBUTO Y NO EN LA HOJA porque depende del valor;
 * la CSP no alcanza a los atributos SVG. Todo lo demás —color, grosor— va por
 * clase, como en el resto de los dibujos.
 *
 * NO ES DECORATIVO (`aria-hidden` no va): a diferencia de la barra, la cifra
 * del centro no está escrita en texto en ningún otro lado de la ficha, y el
 * `<text>` de SVG sí lo leen los lectores de pantalla.
 */
export function Anillo({
  valor,
  escala,
}: {
  valor: number;
  escala: { minimo: number; maximo: number };
}) {
  const recorrido = escala.maximo - escala.minimo;
  const fraccion =
    recorrido <= 0 ? 0 : Math.max(0, Math.min(1, (valor - escala.minimo) / recorrido));
  const arco = fraccion * ANILLO_VUELTA;

  return (
    <svg className="anillo" viewBox={`0 0 ${String(ANILLO_LADO)} ${String(ANILLO_LADO)}`}>
      <circle className="anillo__fondo" cx={ANILLO_CENTRO} cy={ANILLO_CENTRO} r={ANILLO_RADIO} />
      {/* Girado -90° para que el arco arranque arriba y no a las tres en punto.
          El color lo hereda de la `--dim` que fija la ficha. */}
      <circle
        className="anillo__arco"
        cx={ANILLO_CENTRO}
        cy={ANILLO_CENTRO}
        r={ANILLO_RADIO}
        strokeDasharray={`${String(arco)} ${String(ANILLO_VUELTA)}`}
        transform={`rotate(-90 ${String(ANILLO_CENTRO)} ${String(ANILLO_CENTRO)})`}
      />
      <text className="anillo__cifra" x={ANILLO_CENTRO} y={ANILLO_CENTRO} dy="0.36em">
        {numero.format(valor)}
      </text>
    </svg>
  );
}

/* ========================================================================== */

const BARRA_LARGO = 120;
const BARRA_ALTO = 10;

/**
 * La barra de una fila del perfil.
 *
 * ES DECORATIVA A PROPÓSITO (`aria-hidden`): el nombre y el número están al lado
 * en texto, así que un lector de pantalla ya tiene el dato entero y repetirlo
 * como «barra de progreso» solo estorba. Esto es lo que se gana al cambiar el
 * `<progress>` por SVG; lo que se pierde —que el `<progress>` se anunciaba
 * solo— se compensa ahí.
 *
 * EL RELLENO SE DIBUJA SIEMPRE DEL LARGO ENTERO y se encoge con `scaleX` desde
 * la hoja de estilos. Así la animación de entrada es CSS puro y, si el
 * navegador no la ejecuta, lo que queda es la barra completa: nunca una barra
 * vacía que miente sobre el puntaje.
 */
export function Barra({
  valor,
  escala,
  umbral,
}: {
  valor: number;
  escala: { minimo: number; maximo: number };
  umbral: number;
}) {
  const recorrido = escala.maximo - escala.minimo;
  const posicion = (v: number): number =>
    recorrido <= 0 ? 0 : Math.max(0, Math.min(1, (v - escala.minimo) / recorrido)) * BARRA_LARGO;

  const largo = Math.max(posicion(valor), 1.5);
  const enUmbral = posicion(umbral);
  const marcaVisible = umbral > escala.minimo && umbral < escala.maximo;

  return (
    <svg
      className="barra"
      viewBox={`0 0 ${String(BARRA_LARGO)} ${String(BARRA_ALTO)}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <rect className="barra__fondo" x="0" y="3" width={BARRA_LARGO} height="4" rx="2" />
      {/* El color no se pasa aquí: lo hereda de la `--dim` que fija el
          contenedor —la fila del perfil o la tarjeta—, que ya sabe de qué
          dimensión es. */}
      <g className="barra__crecer">
        <rect className="barra__relleno" x="0" y="3" width={largo} height="4" rx="2" />
      </g>
      {marcaVisible && (
        <line className="barra__umbral" x1={enUmbral} y1="0" x2={enUmbral} y2={BARRA_ALTO} />
      )}
    </svg>
  );
}
